/* AJBIR Atlas — « Trouvez votre ambiance »
 * The visitor describes a wish in a sentence, or answers three questions (room, ambiance, budget); the theme composes a
 * set of pieces that go together, from the products in stock, and offers to add the whole set to the cart.
 * Everything runs in the browser on the store's own data (section aj-ambiance-data): no outside service, no key.
 * The engine (top) is plain functions so it can also be checked outside the browser.
 */
(function (root) {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Engine
   * ------------------------------------------------------------------ */
  const fold = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/œ/g, 'oe')
      .replace(/æ/g, 'ae')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[’'`]/g, ' ')
      .replace(/[^a-z0-9,./%+-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  // word starts only ("bol" finds "bols", not "symbole"); a word ending with a space must be whole ("sac ")
  const has = (text, word) => (' ' + text + ' ').includes(' ' + word);

  // What each product is, read from its type (reliable) then its title. First match wins.
  const ROLES = [
    ['skip', ['box cadeau', 'coffret', 'carte cadeau', 'emballage', 'miel', 'amlou', 'argan', 'rouge a levres', 'marque page', 'porte feuille', 'porte monnaie',
      'jouet', 'cuillere', 'spatule', 'porte oeuf', 'edition imparfaite', 'foulard', 'jupe', 'tenue', 'bijou', 'collier', 'chapeau', 'ceinture', 'sac ',
      'panier sac', 'can 2025', 'speciale can', 'bali']],
    ['tapis', ['tapis']],
    ['textile', ['housse de coussin', 'coussin', 'plaid', 'pouf', 'couverture', 'jete de']],
    ['bain', ['salle de bain', 'porte savon', 'porte-savon', 'porte brosse', 'porte-brosse', 'porte serviette', 'porte-serviette', 'porte papier', 'tassa', 'hammam',
      'distributeur de savon', 'savon']],
    ['lumiere', ['suspension', 'abat-jour', 'abat jour', 'applique', 'lanterne', 'lampe', 'luminaire', 'bougeoir', 'porte bougie', 'photophore']],
    ['parfum', ['bougie', 'huile a bruler', 'huile', 'diffuseur', 'spray', 'encens', 'brule parfum']],
    ['linge', ['set de table', 'sets de table', 'napperon', 'chemin de table', 'sous tasse', 'sous-tasse', 'dessous de verre', 'ronds de serviette', 'rond de serviette']],
    ['mobilier', ['tabouret', 'table basse', 'table d appoint', 'tables', 'table doree', 'table en bois', 'table carree', 'table traditionnelle', 'console', 'etagere']],
    ['miroir', ['miroir']],
    ['service', ['plateau', 'theiere', 'carafe', 'sucrier', 'tagine', 'tajine', 'siniya', 'pot a lait', 'porte theiere', 'aspersoir', 'porte sel']],
    ['boire', ['verre', 'tasse', 'mug', 'gobelet', 'verrine']],
    ['vaisselle', ['assiette', 'bol', 'saladier', 'plat ', 'coupelle']],
    ['rangement', ['panier', 'corbeille', 'boite', 'boîte']],
    ['deco', ['vase', 'plante', 'pampa', 'bouquet', 'cadre', 'deco murale', 'pack deco', 'statue', 'oeuf decoratif', 'tefour', 'cactus', 'feuille de palmier', 'pot ']],
  ];
  const roleOf = (p) => {
    const type = fold(p.y);
    const title = fold(p.t);
    let role = null;
    for (const [key, list] of ROLES) {
      if (list.some((w) => has(type, w))) {
        role = key;
        break;
      }
    }
    if (!role) {
      for (const [key, list] of ROLES) {
        if (list.some((w) => has(title, w))) {
          role = key;
          break;
        }
      }
    }
    // a mirror filed under wall decoration is still a mirror; gift boxes and the like never go in a set
    if (role === 'deco' && has(title, 'miroir')) role = 'miroir';
    if (role !== 'skip' && ['coffret', 'carte cadeau', 'can 2025', 'edition imparfaite'].some((w) => has(title, w))) role = 'skip';
    return role;
  };

  // The pieces of a set, for each room: a role per place, with other roles to fall back on.
  const ROOMS = {
    salon: [{ role: 'tapis' }, { role: 'textile' }, { role: 'lumiere' }, { role: 'deco', alt: ['miroir', 'mobilier', 'rangement'] }],
    chambre: [{ role: 'textile', prefer: ['plaid'] }, { role: 'tapis' }, { role: 'lumiere' }, { role: 'parfum', alt: ['deco', 'miroir'] }],
    table: [{ role: 'vaisselle' }, { role: 'boire' }, { role: 'service' }, { role: 'linge', alt: ['deco'] }],
    entree: [{ role: 'miroir', neutral: true }, { role: 'rangement', alt: ['mobilier'] }, { role: 'lumiere', alt: ['deco'] }, { role: 'deco', alt: ['parfum'] }],
    bain: [{ role: 'bain', neutral: true }, { role: 'bain', neutral: true }, { role: 'parfum' }, { role: 'rangement', alt: ['deco'] }],
  };
  // How much of the budget each piece may take (the rug is the big piece of a living room)
  const SHARE = { tapis: 0.5, mobilier: 0.4, textile: 0.25, lumiere: 0.3, miroir: 0.3, service: 0.3, vaisselle: 0.3, boire: 0.2, linge: 0.15, deco: 0.2, rangement: 0.2, parfum: 0.15, bain: 0.3 };

  const words = (list) =>
    String(list || '')
      .split(/[,;\n]/)
      .map((w) => fold(w))
      .filter(Boolean);

  const ready = (amb) => {
    if (amb && !amb._words) amb._words = words(amb.words);
    return amb;
  };

  function prepare(products) {
    products.forEach((p) => {
      if (p._ready) return;
      p._title = fold(p.t);
      p._text = fold([p.t, p.o, p.y, (p.g || []).join(' '), p.v].join(' | '));
      p._tags = (p.g || []).map(fold);
      p._role = roleOf(p);
      p._ready = true;
    });
    return products;
  }

  // How well a product fits an ambiance: its words in the title count double; a tag "ambiance-<key>" is decisive.
  function fit(p, amb, boosts) {
    let s = 0;
    if (amb) {
      for (const w of amb._words) {
        if (has(p._title, w)) s += 2;
        else if (has(p._text, w)) s += 1;
      }
      if (p._tags.includes('ambiance-' + amb.key) || p._tags.includes('ambiance ' + amb.key)) s += 8;
    }
    // the visitor's own words (bois, laine, miroir…) weigh more than the ambiance's
    if (boosts) for (const w of boosts) s += has(p._title, w) ? 3 : has(p._text, w) ? 2 : 0;
    return s;
  }

  function candidatesFor(products, slot, amb, boosts, used, companion) {
    const roles = [slot.role, ...(slot.alt || [])];
    const out = [];
    roles.forEach((role, rank) => {
      const mine = [];
      products.forEach((p) => {
        if (p._role !== role || used.has(p.i)) return;
        let s = fit(p, amb, boosts);
        if (slot.prefer && slot.prefer.some((w) => has(p._title, w))) s += 2;
        if (s <= 0 && amb) return; // off the ambiance: not proposed
        mine.push({ p, s: s - rank * 3, role });
      });
      // nothing in this ambiance for the main piece (e.g. no brass rug): a piece of its companion ambiance, ranked lower
      if (!mine.length && rank === 0 && companion) {
        products.forEach((p) => {
          if (p._role !== role || used.has(p.i)) return;
          const s = fit(p, companion, boosts);
          if (s > 0) mine.push({ p, s: Math.min(s, 4) - 2, role });
        });
      }
      // pieces that suit any style (a mirror for the entrance, brass for the bathroom) when the ambiance has none
      if (!mine.length && rank === 0 && slot.neutral) {
        products.forEach((p) => {
          if (p._role === role && !used.has(p.i)) mine.push({ p, s: -3, role });
        });
      }
      out.push(...mine);
    });
    return out;
  }

  /* Compose a set: one piece per place, the best fits that hold together within the budget (cents, 0 = no limit).
     A small search over the few best candidates of each place: as many pieces as possible, then the best fit,
     then prices close to each piece's share of the budget. */
  function compose(products, opts) {
    prepare(products);
    const room = ROOMS[opts.room] ? opts.room : 'salon';
    const amb = ready(opts.ambiance || null);
    const boosts = (opts.boosts || []).map(fold);
    const budget = opts.budget || 0;
    const slots = ROOMS[room];
    const companion = ready(opts.companion || null);
    const lists = slots.map((slot) =>
      candidatesFor(products, slot, amb, boosts, new Set(), companion)
        .filter((c) => !budget || c.p.p <= budget)
        .sort((a, b) => b.s - a.s || a.p.p - b.p.p)
    );
    // without a budget, a sensible price for each piece: a little above the usual price of its kind
    const target = lists.map((l, i) => {
      if (budget) return budget * (SHARE[slots[i].role] || 0.25);
      const prices = l.map((c) => c.p.p).sort((x, y) => x - y);
      return prices.length ? prices[Math.floor(prices.length / 2)] * 1.3 : 1;
    });
    const short = lists.map((l) => {
      // the best few, plus the cheapest good fits so a small budget still gets a full set
      const seen = new Set();
      const pick = [...l.slice(0, 8), ...[...l].sort((a, b) => a.p.p - b.p.p).filter((c) => c.s >= (l[0] ? l[0].s - 2 : 0)).slice(0, 4)];
      return pick.filter((c) => (seen.has(c.p.i) ? false : seen.add(c.p.i)));
    });
    let best = null;
    const chosen = [];
    const walk = (i, spent, value, ids) => {
      if (i === slots.length) {
        if (!best || value > best.value) best = { value, picks: chosen.slice() };
        return;
      }
      for (const c of short[i]) {
        if (ids.has(c.p.i) || (budget && spent + c.p.p > budget)) continue;
        const off = Math.min(Math.abs(c.p.p - target[i]) / (target[i] || 1), 3);
        chosen[i] = c;
        ids.add(c.p.i);
        walk(i + 1, spent + c.p.p, value + 1000 + c.s * 12 - off * 4, ids);
        ids.delete(c.p.i);
      }
      chosen[i] = null;
      walk(i + 1, spent, value, ids);
    };
    walk(0, 0, 0, new Set());
    const items = [];
    (best ? best.picks : []).forEach((c, i) => {
      if (!c) return;
      const options = lists[i].map((x) => x.p);
      items.push({ slot: slots[i], role: c.role, product: c.p, options, index: options.indexOf(c.p) });
    });
    return { room, ambiance: amb ? amb.key : null, budget, items, total: items.reduce((a, x) => a + x.product.p, 0) };
  }

  /* Another idea for one piece: the next product of its list that keeps the set within the budget. */
  function swap(look, n) {
    const item = look.items[n];
    if (!item) return look;
    const others = look.items.reduce((a, x, i) => (i === n ? a : a + x.product.p), 0);
    const taken = new Set(look.items.map((x) => x.product.i));
    for (let k = 1; k <= item.options.length; k += 1) {
      const j = (item.index + k) % item.options.length;
      const p = item.options[j];
      if (taken.has(p.i) && p !== item.product) continue;
      if (look.budget && others + p.p > look.budget) continue;
      item.index = j;
      item.product = p;
      break;
    }
    look.total = look.items.reduce((a, x) => a + x.product.p, 0);
    return look;
  }

  /* More pieces in the same ambiance (and room), best fit first */
  function more(products, look, opts, limit = 12) {
    const amb = ready(opts.ambiance);
    const boosts = (opts.boosts || []).map(fold);
    const roles = new Set(ROOMS[look.room].flatMap((s) => [s.role, ...(s.alt || [])]));
    const taken = new Set(look.items.map((x) => x.product.i));
    return products
      .filter((p) => p._role && p._role !== 'skip' && !taken.has(p.i) && roles.has(p._role))
      .map((p) => ({ p, s: fit(p, amb, boosts) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, limit)
      .map((x) => x.p);
  }

  /* A sentence → room, ambiance, budget, colours and materials to favour. */
  const ROOM_WORDS = {
    salon: ['salon', 'sejour', 'living', 'canape', 'piece a vivre'],
    chambre: ['chambre', 'lit', 'nuit', 'dormir', 'cosy', 'cocon'],
    table: ['table', 'salle a manger', 'cuisine', 'diner', 'repas', 'recevoir', 'invites', 'the ', 'cafe', 'brunch', 'vaisselle'],
    entree: ['entree', 'couloir', 'hall', 'accueil'],
    bain: ['salle de bain', 'sdb', 'bain', 'hammam', 'douche'],
  };
  const MOODS = {
    riad: ['riad', 'oriental', 'traditionnel', 'luxe', 'chic', 'dore', 'precieux', 'fes', 'marrakech', 'raffine'],
    boheme: ['boheme', 'boho', 'nature', 'naturel', 'plage', 'vacances', 'detente', 'decontracte', 'fibres'],
    minimal: ['minimal', 'minimaliste', 'epure', 'sobre', 'zen', 'scandinave', 'lumineux', 'neutre', 'calme', 'moderne'],
    terre: ['terre', 'chaleureux', 'chaud', 'chaude', 'ocre', 'rustique', 'authentique', 'automne'],
    souk: ['colore', 'couleur', 'couleurs', 'gai', 'joyeux', 'festif', 'vif', 'pop', 'motifs', 'enfant'],
  };
  // objects named in a wish: the set favours them
  const OBJECTS = {
    the: ['theiere', 'verre', 'plateau', 'siniya', 'beldi'], cafe: ['tasse', 'cafe', 'mug'], miroir: ['miroir'], tapis: ['tapis'], coussin: ['coussin'],
    plaid: ['plaid'], lampe: ['suspension', 'abat-jour', 'lanterne', 'applique'], lumiere: ['suspension', 'abat-jour', 'lanterne', 'applique'],
    luminaire: ['suspension', 'abat-jour', 'lanterne', 'applique'], suspension: ['suspension'], bougie: ['bougie'], vase: ['vase'], plante: ['pampas', 'bouquet', 'plante'],
    fleur: ['pampas', 'bouquet'], panier: ['panier'], assiette: ['assiette'], tabouret: ['tabouret'], table: [], plateau: ['plateau'],
  };
  const COLOURS = ['blanc', 'creme', 'ecru', 'beige', 'sable', 'taupe', 'gris', 'noir', 'marron', 'terracota', 'terracotta', 'rouge', 'bordeaux', 'rose', 'corail', 'orange',
    'moutarde', 'jaune', 'vert', 'turquoise', 'bleu', 'petrole', 'indigo', 'violet', 'mauve', 'dore', 'argente', 'multicolore'];
  const MATERIALS = ['laiton', 'cuivre', 'bois', 'laine', 'raphia', 'rotin', 'palmier', 'osier', 'paille', 'ceramique', 'verre', 'terre cuite', 'cuir', 'coton', 'lin', 'soie', 'resine', 'zellige'];

  function readBudget(t) {
    const m = t.match(/(?:moins de|max(?:imum)?|budget(?: de)?|jusqu a|sous|pas plus de|environ|autour de|a)\s*(\d[\d\s.]*)\s*(k|mille)?/) || t.match(/(\d[\d\s.]*)\s*(k|mille)?\s*(?:dh|dhs|mad|dirham)/);
    if (!m) return 0;
    let n = parseInt(m[1].replace(/[\s.]/g, ''), 10);
    if (m[2]) n *= 1000;
    return n >= 50 ? n * 100 : 0;
  }

  function parseWish(text, ambiances) {
    const t = ' ' + fold(text) + ' ';
    const out = { room: null, ambiance: null, budget: readBudget(t), colours: [], materials: [] };
    let best = 0;
    for (const [room, list] of Object.entries(ROOM_WORDS)) {
      const n = list.filter((w) => t.includes(' ' + w)).length;
      if (n > best) {
        best = n;
        out.room = room;
      }
    }
    let top = 0;
    ambiances.forEach((amb) => {
      ready(amb);
      const name = fold(amb.name);
      let n = (MOODS[amb.key] || []).filter((w) => t.includes(' ' + w)).length * 2;
      n += amb._words.filter((w) => w.length > 3 && t.includes(' ' + w)).length;
      if (name && t.includes(name)) n += 4;
      if (n > top) {
        top = n;
        out.ambiance = amb.key;
      }
    });
    out.colours = COLOURS.filter((w) => t.includes(' ' + w));
    out.materials = MATERIALS.filter((w) => t.includes(' ' + w));
    out.objects = [];
    out.named = [];
    for (const [w, list] of Object.entries(OBJECTS)) {
      if (!t.includes(' ' + w)) continue;
      out.objects.push(...list);
      if (list.length) out.named.push(w);
    }
    // no room named: the objects tell it (« une théière et des verres » is for the table, « un miroir » for the entrance)
    if (!out.room) {
      const OBJECT_ROOM = { the: 'table', cafe: 'table', assiette: 'table', plateau: 'table', miroir: 'entree' };
      const named = out.named.find((w) => OBJECT_ROOM[w]);
      if (named) out.room = OBJECT_ROOM[named];
    }
    out.gift = /\b(cadeau|offrir|offre|anniversaire)\b/.test(t);
    return out;
  }

  // The words understood, written back with their accents for display
  const PRETTY = {
    creme: 'crème', ecru: 'écru', dore: 'doré', argente: 'argenté', petrole: 'pétrole', ceramique: 'céramique', resine: 'résine',
    the: 'thé', cafe: 'café', lumiere: 'lumière', terracota: 'terracotta',
  };
  const pretty = (w) => PRETTY[w] || w;

  const Engine = { fold, roleOf, ready, prepare, fit, compose, swap, more, parseWish, pretty, ROOMS };
  if (typeof module === 'object' && module.exports) module.exports = Engine;
  if (!root || !root.document) return;

  /* ------------------------------------------------------------------ *
   * Interface
   * ------------------------------------------------------------------ */
  const AJ = (root.AJBIR = root.AJBIR || {});
  AJ.AmbianceEngine = Engine;
  const doc = root.document;
  const qs = (sel, el) => (el || doc).querySelector(sel);
  const qsa = (sel, el) => Array.from((el || doc).querySelectorAll(sel));
  const money = (cents) => (AJ.formatMoney ? AJ.formatMoney(cents) : (cents / 100).toFixed(0));
  const moneyShort = (cents) => money(cents).replace(/([.,])00(?!\d)/, '');
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const sized = (url, w) => (url ? url.replace(/([?&])width=\d+/, '$1width=' + w) : '');
  const reduced = () => Boolean(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const svg = (paths, size, cls) =>
    `<svg class="aj-icon ${cls || ''}" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${paths}</svg>`;
  const ICON = {
    swap: svg('<path d="M4 9h13l-3.5-3.5M20 15H7l3.5 3.5"/>', 16),
    plus: svg('<path d="M12 5v14M5 12h14"/>', 18, 'aj-amb__add-plus'),
    check: svg('<path d="M5 12.5l4.2 4.2L19 7"/>', 18, 'aj-amb__add-check'),
    restart: svg('<path d="M4.6 12a7.4 7.4 0 1 0 2.2-5.25"/><path d="M4.6 4.4v3.3h3.3"/>', 16),
    chevron: svg('<path d="M6 9l6 6 6-6"/>', 14, 'aj-amb__select-icon'),
    arrow: svg('<path d="M4 12h15M14 7l5 5-5 5"/>', 16),
  };

  // The catalogue in stock, read once per visit (pages of 250 products), kept for the session
  const Data = {
    promises: {},
    load(url) {
      if (this.promises[url]) return this.promises[url];
      const key = 'aj_ambiance_v1_' + url;
      try {
        const cached = root.sessionStorage.getItem(key);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.at > Date.now() - 30 * 60 * 1000 && Array.isArray(parsed.products)) return (this.promises[url] = Promise.resolve(parsed.products));
        }
      } catch (e) {
        /* storage unavailable */
      }
      const page = (n) =>
        fetch(url + (url.includes('?') ? '&' : '?') + 'page=' + n, { credentials: 'same-origin' })
          .then((r) => {
            if (!r.ok) throw new Error(String(r.status));
            return r.text();
          })
          .then((html) => {
            const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-aj-ambiance-json]');
            if (!node) throw new Error('no data');
            return JSON.parse(node.textContent);
          });
      const promise = page(1).then(async (first) => {
        const pages = Math.max(1, Math.min(parseInt(first.pages, 10) || 1, 12));
        let partial = false;
        const rest = await Promise.all(
          Array.from({ length: pages - 1 }, (_, i) =>
            page(i + 2).catch(() => {
              partial = true; // compose with what came, but don't keep an incomplete catalogue
              return { products: [] };
            })
          )
        );
        const seen = new Set();
        const all = [first, ...rest].flatMap((d) => d.products || []).filter((p) => p && p.i && p.d && (seen.has(p.i) ? false : seen.add(p.i)));
        try {
          if (!partial) root.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), products: all }));
        } catch (e) {
          /* full or unavailable */
        }
        if (partial) setTimeout(() => delete this.promises[url], 0);
        return all;
      });
      this.promises[url] = promise;
      promise.catch(() => {
        if (this.promises[url] === promise) delete this.promises[url];
      });
      return promise;
    },
  };

  class Finder {
    constructor(el) {
      this.el = el;
      const conf = qs('[data-aj-ambiance-config]', el);
      let cfg = {};
      try {
        cfg = conf ? JSON.parse(conf.textContent) : {};
      } catch (e) {
        cfg = {};
      }
      // translations arrive HTML-escaped (l&#39;ensemble): back to plain text, escaped again where they are inserted
      const decoder = doc.createElement('textarea');
      const plain = (v) => {
        if (typeof v !== 'string' || !v.includes('&')) return v;
        decoder.innerHTML = v;
        return decoder.value;
      };
      const plainAll = (o) => Object.keys(o || {}).reduce((acc, k) => ((acc[k] = plain(o[k])), acc), {});
      cfg.ambiances = (cfg.ambiances || []).filter((a) => a && a.key);
      cfg.rooms = (cfg.rooms || []).filter((r) => r && Engine.ROOMS[r.key]).map((r) => ({ key: r.key, label: plain(r.label), inLabel: plain(r.inLabel) }));
      cfg.budgets = (cfg.budgets || []).map((b) => parseInt(b, 10) || 0);
      cfg.roles = plainAll(cfg.roles);
      cfg.strings = plainAll(cfg.strings);
      this.cfg = cfg;
      this.t = new Proxy(cfg.strings || {}, { get: (o, k) => (typeof o[k] === 'string' ? o[k] : '') });
      this.stage = qs('[data-aj-amb-stage]', el);
      this.live = qs('[data-aj-amb-live]', el);
      this.input = qs('[data-aj-amb-wish-input]', el);
      this.reset();
      el.addEventListener('click', (e) => this.onClick(e));
      el.addEventListener('change', (e) => this.onChange(e));
      const form = qs('[data-aj-amb-wish]', el);
      if (form) form.addEventListener('submit', (e) => this.onWish(e));
      // The catalogue loads when the section comes into view or is touched, so the answer comes at once
      const warm = () => this.warm();
      ['pointerdown', 'focusin', 'mouseover', 'touchstart'].forEach((type) => el.addEventListener(type, warm, { once: true, passive: true }));
      const conn = root.navigator && root.navigator.connection;
      if ('IntersectionObserver' in root && !(conn && conn.saveData)) {
        const io = new IntersectionObserver(
          (entries) => {
            if (!entries.some((x) => x.isIntersecting)) return;
            io.disconnect();
            warm();
          },
          { rootMargin: '200px 0px' }
        );
        io.observe(el);
      }
    }

    reset() {
      this.state = { room: null, ambiance: null, budget: 0, boosts: [], shown: [], from: 'steps', wish: null, picked: new Set() };
      this.look = null;
      this.extra = [];
      this.el.classList.remove('needs-ambiance', 'is-gift');
    }

    products() {
      if (!this.cfg.dataUrl) return Promise.reject(new Error('no data url'));
      return Data.load(this.cfg.dataUrl).then((list) => Engine.prepare(list));
    }

    warm() {
      if (!this.warming) {
        this.warming = this.products()
          .then(() => this.paintMoods())
          .catch(() => {
            this.warming = null;
          });
      }
      return this.warming;
    }

    ambiance(key) {
      return this.cfg.ambiances.find((a) => a.key === key) || null;
    }

    say(text) {
      if (!this.live) return;
      clearTimeout(this.sayTimer);
      this.live.textContent = '';
      this.sayTimer = setTimeout(() => {
        this.live.textContent = text || '';
      }, 80);
    }

    // Each ambiance card shows three of the store's own pieces in that mood (pieces of different kinds)
    paintMoods() {
      return this.products().then((list) => {
        const usable = list.filter((p) => p._role && p._role !== 'skip' && p.m);
        qsa('[data-aj-amb-mood]', this.el).forEach((card) => {
          const amb = this.ambiance(card.dataset.ajAmbMood);
          const holder = qs('[data-aj-amb-thumbs]', card);
          if (!amb || !holder || holder.dataset.done) return;
          Engine.ready(amb);
          const scored = usable
            .map((p) => ({ p, s: Engine.fit(p, amb, []) }))
            .filter((x) => x.s > 0)
            .sort((a, b) => b.s - a.s || a.p.i - b.p.i);
          const picks = [];
          const roles = new Set();
          [3, 1].forEach((min) =>
            scored.forEach(({ p, s }) => {
              if (picks.length < 3 && s >= min && !roles.has(p._role)) {
                roles.add(p._role);
                picks.push(p);
              }
            })
          );
          if (!picks.length) return;
          holder.innerHTML = picks.map((p, i) => `<img src="${esc(sized(p.m, i ? 240 : 360))}" alt="" loading="lazy" decoding="async" width="${i ? 120 : 180}" height="${i ? 120 : 225}">`).join('');
          holder.dataset.count = String(picks.length);
          holder.dataset.done = '1';
          card.classList.add('has-thumbs');
        });
      });
    }

    onClick(e) {
      const target = e.target;
      if (!target || !target.closest) return;
      const example = target.closest('[data-aj-amb-example]');
      if (example) {
        e.preventDefault();
        const text = example.textContent.trim();
        if (this.input) this.input.value = text;
        this.wish(text);
        return;
      }
      const go = target.closest('[data-aj-amb-go]');
      if (go) {
        e.preventDefault();
        const step = go.dataset.ajAmbGo;
        if (step === 'restart') this.reset();
        if (step === 'room') {
          // the three questions: a fresh guided run
          this.state.from = 'steps';
          this.el.classList.remove('needs-ambiance');
        }
        this.show(step === 'restart' ? 'start' : step, { keyboard: e.detail === 0 });
        return;
      }
      const pick = target.closest('[data-aj-amb-pick]');
      if (pick) {
        e.preventDefault();
        this.pick(pick.dataset.ajAmbPick);
        return;
      }
      const swap = target.closest('[data-aj-amb-swap]');
      if (swap && this.look) {
        e.preventDefault();
        const n = parseInt(swap.dataset.ajAmbSwap, 10);
        Engine.swap(this.look, n);
        this.updateItem(n);
        const item = this.look.items[n];
        this.say(this.t.swapped.replace('[title]', item ? item.product.t : ''));
        return;
      }
      if (target.closest('[data-aj-amb-retry]')) {
        e.preventDefault();
        this.result();
        return;
      }
      const all = target.closest('[data-aj-amb-addall]');
      if (all && this.look) {
        e.preventDefault();
        this.addAll(all);
        return;
      }
      const one = target.closest('[data-aj-amb-add]');
      if (one) {
        e.preventDefault();
        this.addOne(one);
      }
    }

    pick(value) {
      const [kind, raw] = String(value).split(':');
      if (!['room', 'ambiance', 'budget'].includes(kind)) return;
      this.state[kind] = kind === 'budget' ? parseInt(raw, 10) || 0 : raw;
      this.state.picked.add(kind);
      // after a sentence that only missed the style, the style was the last question
      if (this.state.from === 'wish' && kind === 'ambiance') {
        this.el.classList.remove('needs-ambiance');
        this.result();
        return;
      }
      this.state.from = 'steps';
      if (kind === 'room') {
        // a new guided run forgets an earlier sentence
        Object.assign(this.state, { boosts: [], shown: [], wish: null });
        this.el.classList.remove('is-gift');
      }
      const next = { room: 'ambiance', ambiance: 'budget', budget: 'result' }[kind];
      if (next === 'result') this.result();
      else this.show(next);
    }

    onChange(e) {
      const sel = e.target && e.target.closest ? e.target.closest('[data-aj-amb-refine]') : null;
      if (!sel || !this.look) return;
      const kind = sel.dataset.ajAmbRefine;
      this.state[kind] = kind === 'budget' ? parseInt(sel.value, 10) || 0 : sel.value;
      this.state.picked.add(kind);
      this.result({ focus: `[data-aj-amb-refine="${kind}"]` });
    }

    onWish(e) {
      e.preventDefault();
      this.wish(this.input ? this.input.value.trim() : '');
    }

    wish(text) {
      if (!text) {
        this.state.from = 'steps';
        this.show('room');
        return;
      }
      const wish = Engine.parseWish(text, this.cfg.ambiances);
      const rooms = this.cfg.rooms.map((r) => r.key);
      const roomFound = rooms.includes(wish.room);
      const ambianceFound = Boolean(this.ambiance(wish.ambiance));
      this.state = {
        room: roomFound ? wish.room : rooms[0] || 'salon',
        ambiance: ambianceFound ? wish.ambiance : null,
        budget: wish.budget || 0,
        boosts: [...wish.colours, ...wish.materials, ...wish.objects],
        shown: [...wish.colours, ...wish.materials, ...wish.named].map(Engine.pretty),
        from: 'wish',
        wish: Object.assign(wish, { roomFound, ambianceFound }),
        text,
        picked: new Set([roomFound && 'room', ambianceFound && 'ambiance', wish.budget && 'budget'].filter(Boolean)),
      };
      this.el.classList.toggle('is-gift', Boolean(wish.gift));
      this.warm();
      if (!ambianceFound) {
        // understood the place (and the budget), not the style: one question left
        this.el.classList.add('needs-ambiance');
        this.show('ambiance');
        this.say(this.t.needAmbiance);
        return;
      }
      this.el.classList.remove('needs-ambiance');
      this.result();
    }

    // The chosen answer of each question stays marked when the visitor goes back
    markPicked() {
      ['room', 'ambiance', 'budget'].forEach((kind) => {
        const chosen = this.state.picked.has(kind);
        qsa(`[data-aj-amb-pick^="${kind}:"]`, this.el).forEach((b) => {
          if (chosen) b.setAttribute('aria-pressed', String(b.dataset.ajAmbPick === `${kind}:${this.state[kind]}`));
          else b.removeAttribute('aria-pressed');
        });
      });
    }

    show(step, opts = {}) {
      qsa('[data-aj-amb-step]', this.el).forEach((s) => {
        s.hidden = s.dataset.ajAmbStep !== step;
      });
      this.el.dataset.step = step;
      this.markPicked();
      const panel = qs(`[data-aj-amb-step="${step}"]`, this.el);
      if (opts.focus !== false && panel) {
        let target = qs('[data-aj-amb-focus]', panel);
        if (step === 'start') target = opts.keyboard && this.input ? this.input : this.stage;
        if (target) target.focus({ preventScroll: true });
      }
      if (opts.scroll === false) return;
      // bring the step into view when the visitor had scrolled past its top (under the header)
      const header = qs('[data-aj-header]');
      const offset = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      if (this.stage.getBoundingClientRect().top < offset) this.stage.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
    }

    result(opts = {}) {
      const out = qs('[data-aj-amb-result]', this.el);
      if (!opts.focus) out.innerHTML = ''; // a new set: never show the previous one, even for a moment
      if (this.el.dataset.step !== 'result') this.show('result', { focus: false });
      out.setAttribute('aria-busy', 'true');
      // a skeleton only if the catalogue is not there yet (no flash when it is)
      const slow = setTimeout(() => {
        if (opts.focus) out.classList.add('is-updating');
        else out.innerHTML = this.skeleton();
        this.say(this.t.loading);
      }, 150);
      this.products()
        .then((list) => {
          clearTimeout(slow);
          this.compose(list);
          this.renderLook(opts.focus ? { focus: opts.focus } : { focus: 'title' });
          this.track('ajbir_ambiance_set_composed', {
            room: this.state.room || '',
            ambiance: this.state.ambiance || '',
            budget: this.state.budget || '',
            from: this.state.from || '',
            items: this.look && this.look.items ? this.look.items.length : 0,
          });
        })
        .catch(() => {
          clearTimeout(slow);
          out.innerHTML = `<div class="aj-amb__empty"><p>${esc(this.t.error)}</p><button type="button" class="aj-btn aj-btn--outline aj-btn--sm" data-aj-amb-retry>${esc(this.t.retry)}</button></div>`;
          this.say(this.t.error);
          const retry = qs('[data-aj-amb-retry]', out);
          if (retry) retry.focus({ preventScroll: true });
        })
        .finally(() => {
          out.removeAttribute('aria-busy');
          out.classList.remove('is-updating');
        });
    }

    // Custom storefront events for pixels (Settings › Customer events)
    track(name, data) {
      if (AJ.track) AJ.track(name, data);
    }

    compose(list) {
      const amb = this.ambiance(this.state.ambiance);
      const companion = amb && amb.companion && amb.companion !== amb.key ? this.ambiance(amb.companion) : null;
      this.look = Engine.compose(list, { room: this.state.room, ambiance: amb, budget: this.state.budget, boosts: this.state.boosts, companion });
      this.extra = Engine.more(list, this.look, { ambiance: amb, boosts: this.state.boosts }, 12);
    }

    skeleton() {
      const card = '<li class="aj-amb__item is-skeleton"><span class="aj-amb__media"></span><span class="aj-amb__info"><span class="aj-amb__sk aj-amb__sk--short"></span><span class="aj-amb__sk"></span><span class="aj-amb__sk aj-amb__sk--short"></span></span></li>';
      return `<div class="aj-amb__skeleton" aria-hidden="true"><div class="aj-amb__head"><span class="aj-amb__sk aj-amb__sk--title"></span><span class="aj-amb__sk aj-amb__sk--text"></span></div><ul class="aj-amb__look" role="list">${card.repeat(4)}</ul></div>`;
    }

    understood() {
      const s = this.state;
      if (s.from !== 'wish' || !s.wish) return [];
      const chips = [];
      if (s.wish.roomFound) {
        const r = this.cfg.rooms.find((x) => x.key === s.wish.room);
        if (r) chips.push(r.label);
      }
      if (s.wish.ambianceFound) {
        const a = this.ambiance(s.wish.ambiance);
        if (a) chips.push(a.name);
      }
      if (s.wish.budget) chips.push(this.t.upTo.replace('[amount]', moneyShort(s.wish.budget)));
      (s.shown || []).forEach((w) => {
        if (w && !chips.includes(w)) chips.push(w);
      });
      return chips;
    }

    refineHTML() {
      const s = this.state;
      const opt = (v, label, on) => `<option value="${esc(v)}"${on ? ' selected' : ''}>${esc(label)}</option>`;
      const budgets = Array.from(new Set([...this.cfg.budgets, s.budget || 0, 0])).sort((a, b) => (a || Infinity) - (b || Infinity));
      const field = (kind, label, options) =>
        `<label class="aj-amb__refine-field"><span class="aj-amb__refine-label">${esc(label)}</span><span class="aj-amb__select-wrap"><select class="aj-amb__select" data-aj-amb-refine="${kind}">${options}</select>${ICON.chevron}</span></label>`;
      return `<div class="aj-amb__refine">${[
        field('room', this.t.refineRoom, this.cfg.rooms.map((r) => opt(r.key, r.label, r.key === s.room)).join('')),
        field('ambiance', this.t.refineAmbiance, this.cfg.ambiances.map((a) => opt(a.key, a.name, a.key === s.ambiance)).join('')),
        field('budget', this.t.refineBudget, budgets.map((b) => opt(b, b ? this.t.budgetUnder.replace('[amount]', moneyShort(b)) : this.t.budgetNone, b === (s.budget || 0))).join('')),
      ].join('')}</div>`;
    }

    itemHTML(x, n, fresh) {
      const p = x.product;
      const label = this.cfg.roles[x.role] || '';
      const img = p.m
        ? `<img src="${esc(sized(p.m, 480))}" srcset="${esc(sized(p.m, 360))} 360w, ${esc(sized(p.m, 480))} 480w, ${esc(sized(p.m, 720))} 720w" sizes="(min-width: 990px) 260px, 45vw" alt="" loading="lazy" decoding="async" width="480" height="600">`
        : '';
      return `<li class="aj-amb__item${fresh ? ' is-new' : ''}" data-aj-amb-item>
            <a class="aj-amb__media" href="${esc(p.u)}" tabindex="-1" aria-hidden="true">${img}</a>
            <div class="aj-amb__info">
              <span class="aj-amb__role">${esc(label)}</span>
              <a class="aj-amb__name" href="${esc(p.u)}">${esc(p.t)}</a>
              <span class="aj-amb__price">${p.o ? `<span class="aj-amb__variant">${esc(p.o)}</span>` : ''}<span>${esc(money(p.p))}</span></span>
            </div>
            ${x.options.length > 1 ? `<button type="button" class="aj-amb__swap" data-aj-amb-swap="${n}" aria-label="${esc(this.t.swapNamed.replace('[title]', p.t))}">${ICON.swap}<span>${esc(this.t.swap)}</span></button>` : ''}
          </li>`;
    }

    // Another idea for one piece: only that card changes (and the total)
    updateItem(n) {
      const out = qs('[data-aj-amb-result]', this.el);
      const li = qsa('[data-aj-amb-item]', out)[n];
      const x = this.look.items[n];
      if (!li || !x) {
        this.renderLook({ focus: `[data-aj-amb-swap="${n}"]`, announce: false });
        return;
      }
      const tmp = doc.createElement('ul');
      tmp.innerHTML = this.itemHTML(x, n, true);
      const fresh = tmp.firstElementChild;
      li.replaceWith(fresh);
      const sum = qs('.aj-amb__sum strong', out);
      if (sum) sum.textContent = money(this.look.total);
      qsa('[data-aj-amb-add]', out).forEach((b) => {
        if (b.dataset.ajAmbAdd !== String(x.product.d)) return;
        const more = b.closest('.aj-amb__more-item');
        if (more) more.remove();
      });
      const btn = qs('[data-aj-amb-swap]', fresh);
      if (btn) btn.focus({ preventScroll: true });
    }

    renderLook(opts = {}) {
      const out = qs('[data-aj-amb-result]', this.el);
      const look = this.look;
      const amb = this.ambiance(this.state.ambiance);
      const room = this.cfg.rooms.find((r) => r.key === look.room) || { label: '', inLabel: '' };
      const title = amb ? this.t.lookTitle.replace('[room]', room.inLabel).replace('[ambiance]', amb.name) : this.t.lookTitlePlain.replace('[room]', room.inLabel);
      const chips = this.understood();
      const gift = this.state.wish && this.state.wish.gift && this.cfg.giftUrl;
      const head = `<div class="aj-amb__head">
          ${amb && amb.colors ? `<span class="aj-amb__palette aj-amb__palette--lg" aria-hidden="true">${amb.colors.map((c) => `<span style="background:${esc(c)}"></span>`).join('')}</span>` : ''}
          <h3 class="aj-amb__title" tabindex="-1" data-aj-amb-result-title>${esc(title)}</h3>
          ${amb && amb.text ? `<p class="aj-amb__text">${esc(amb.text)}</p>` : ''}
          ${chips.length ? `<p class="aj-amb__understood"><span class="aj-amb__understood-label">${esc(this.t.understood)}</span> ${chips.map((c) => `<span class="aj-amb__chip">${esc(c)}</span>`).join(' ')}</p>` : ''}
          ${gift ? `<p class="aj-amb__gift"><a class="aj-link" href="${esc(this.cfg.giftUrl)}">${esc(this.t.giftHint)}${ICON.arrow}</a></p>` : ''}
        </div>
        ${this.refineHTML()}`;
      if (!look.items.length) {
        out.innerHTML = `${head}<div class="aj-amb__empty"><p>${esc(this.t.none)}</p><button type="button" class="aj-btn aj-btn--outline aj-btn--sm" data-aj-amb-go="restart">${ICON.restart}${esc(this.t.restart)}</button></div>`;
        this.after(out, opts, `${title}. ${this.t.none}`);
        return;
      }
      const items = look.items.map((x, n) => this.itemHTML(x, n, false)).join('');
      const count = look.items.length;
      const slots = (Engine.ROOMS[look.room] || []).length;
      const totalLabel = (count === 1 ? this.t.totalOne : this.t.totalOther).replace('[count]', count);
      const note = look.budget && count < slots ? `<p class="aj-amb__note">${esc(this.t.partial.replace('[count]', count).replace('[total]', slots))}</p>` : '';
      const extra = (this.extra || [])
        .filter((p) => !look.items.some((x) => x.product.i === p.i))
        .slice(0, 8)
        .map(
          (p) => `<li class="aj-amb__more-item">
            <a class="aj-amb__more-media" href="${esc(p.u)}" tabindex="-1" aria-hidden="true">${p.m ? `<img src="${esc(sized(p.m, 360))}" alt="" loading="lazy" decoding="async" width="360" height="450">` : ''}</a>
            <a class="aj-amb__more-name" href="${esc(p.u)}">${esc(p.t)}</a>
            <span class="aj-amb__more-row"><span class="aj-amb__price">${esc(money(p.p))}</span><button type="button" class="aj-amb__add" data-aj-amb-add="${esc(p.d)}" data-title="${esc(p.t)}" data-detail="${esc(p.o || '')}" data-image="${esc(sized(p.m, 240))}" aria-label="${esc(this.t.addNamed.replace('[title]', p.t))}">${ICON.plus}${ICON.check}</button></span>
          </li>`
        )
        .join('');
      out.innerHTML = `${head}
        <ul class="aj-amb__look" role="list" data-count="${count}">${items}</ul>
        ${note}
        <div class="aj-amb__total">
          <p class="aj-amb__sum"><span>${esc(totalLabel)}</span><strong>${esc(money(look.total))}</strong></p>
          <div class="aj-amb__total-actions">
            <button type="button" class="aj-btn aj-btn--primary aj-amb__addall" data-aj-amb-addall><span data-aj-atc-label>${esc(this.t.addAll)}</span></button>
            <button type="button" class="aj-amb__again" data-aj-amb-go="restart">${ICON.restart}<span>${esc(this.t.restart)}</span></button>
          </div>
        </div>
        ${extra ? `<div class="aj-amb__more"><h4 class="aj-amb__more-title">${esc(this.t.moreTitle)}</h4><ul class="aj-amb__more-list" role="list">${extra}</ul></div>` : ''}`;
      this.after(out, opts, `${title}. ${totalLabel} : ${money(look.total)}`);
    }

    after(out, opts, message) {
      const target = opts.focus === 'title' ? qs('[data-aj-amb-result-title]', out) : opts.focus ? qs(opts.focus, out) : null;
      if (target) target.focus({ preventScroll: true });
      if (opts.announce !== false) this.say(message);
    }

    // Adds through the theme's cart (drawer or confirmation, as set in the theme settings)
    async add(button, items, item) {
      const Cart = AJ.Cart;
      if (!Cart || button.classList.contains('is-loading')) return false;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      let early = false;
      try {
        if (Cart.opensOnAdd && Cart.opensOnAdd()) {
          early = true;
          Cart.pending(true, button, item);
        }
        await Cart.addItems(items, item);
        return true;
      } catch (err) {
        if (early) {
          Cart.pending(false);
          const drawer = Cart.usesDrawer && Cart.usesDrawer();
          if (drawer && AJ.Layers) AJ.Layers.close(drawer);
        }
        if (AJ.toast) AJ.toast(err && err.message ? err.message : this.t.error);
        return false;
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }

    async addAll(button) {
      const look = this.look;
      const items = look.items.map((x) => ({ id: parseInt(x.product.d, 10), quantity: 1 }));
      const amb = this.ambiance(this.state.ambiance);
      const count = items.length;
      const item = {
        title: (count === 1 ? this.t.addedSetOne : this.t.addedSetOther).replace('[count]', count),
        detail: amb ? amb.name : '',
        image: sized(look.items[0].product.m, 240),
      };
      if (!(await this.add(button, items, item))) return;
      this.track('ajbir_ambiance_set_added', { ambiance: this.state.ambiance || '', items: count, variant_ids: items.map((x) => x.id) });
      const label = qs('[data-aj-atc-label]', button);
      const before = label ? label.textContent : '';
      button.classList.add('is-added');
      if (label && this.t.added) label.textContent = this.t.added;
      setTimeout(() => {
        button.classList.remove('is-added');
        if (label) label.textContent = before;
      }, 2200);
    }

    async addOne(button) {
      const item = { title: button.dataset.title || '', detail: button.dataset.detail || '', image: button.dataset.image || '' };
      if (!(await this.add(button, [{ id: parseInt(button.dataset.ajAmbAdd, 10), quantity: 1 }], item))) return;
      this.track('ajbir_ambiance_item_added', { ambiance: this.state.ambiance || '', variant_id: parseInt(button.dataset.ajAmbAdd, 10) });
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1800);
    }
  }

  const init = (scope) => {
    qsa('[data-aj-ambiance]', scope).forEach((el) => {
      if (!el.ajFinder) el.ajFinder = new Finder(el);
    });
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => init());
  else init();
  doc.addEventListener('shopify:section:load', (e) => init(e.target));
  // Theme editor: selecting an ambiance block shows the ambiance cards
  doc.addEventListener('shopify:block:select', (e) => {
    const el = e.target && e.target.closest ? e.target.closest('[data-aj-ambiance]') : null;
    if (el && el.ajFinder) el.ajFinder.show('ambiance', { focus: false, scroll: false });
  });
  // A link to the section on the same page (e.g. from the search window): close the window, go to the section
  doc.addEventListener('click', (e) => {
    const link = e.target && e.target.closest ? e.target.closest('a[href*="#"]') : null;
    if (!link || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    let url;
    try {
      url = new URL(link.href, root.location.href);
    } catch (err) {
      return;
    }
    if (url.pathname !== root.location.pathname || url.origin !== root.location.origin || url.hash.length < 2) return;
    let section = null;
    try {
      section = doc.getElementById(decodeURIComponent(url.hash.slice(1)));
    } catch (err) {
      return;
    }
    if (!section || !section.matches('[data-aj-ambiance]')) return;
    e.preventDefault();
    const layer = link.closest('[data-aj-search], [data-aj-drawer]');
    if (layer && AJ.Layers) AJ.Layers.close(layer, false);
    section.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
    const finder = section.ajFinder;
    if (!finder) return;
    if (finder.el.dataset.step !== 'start' && !finder.look) finder.show('start', { focus: false, scroll: false });
    // keyboard: straight into the sentence field; touch: no keyboard popping up
    const target = e.detail === 0 && finder.input && finder.el.dataset.step === 'start' ? finder.input : finder.stage;
    if (target) target.focus({ preventScroll: true });
  });
})(typeof window !== 'undefined' ? window : null);
